// Lead Generation System — manager-editable compensation plan API client
// (`api/lead-gen/compensation-plan`). Reads gate on LeadGen.View; saving a new
// effective-dated version gates on LeadGen.ManagePlan. DISPLAY-ONLY: the plan
// feeds the standalone lead-gen earnings ledger; no financial-subsystem
// coupling, and a saved version applies to NEW events only.

import { api } from "@/api/client";

export type LeadGenPlanStatus = "Active" | "Superseded";

/** The manager-editable knob-set. Rates are dollar amounts (0.10 = $0.10, NOT
 *  a fraction). Caps + daily count limits are nullable (null = unset / no cap
 *  on that axis). Multipliers are decimal factors (1.00 = 100%). Grade weights
 *  are whole-number weights that sum to 100. */
export interface LeadGenPlanConfig {
  // Per-KPI dollar rates.
  prospectCreatedRate: number;
  ndaCompletedRate: number;
  cimReceivedRate: number;
  interestedEscalationRate: number;
  completedAppointmentRate: number;

  // Per-bucket weekly caps.
  outreachWeeklyCap?: number | null;
  ndaCimWeeklyCap?: number | null;
  interestedEscalationsWeeklyCap?: number | null;
  appointmentsWeeklyCap?: number | null;
  totalWeeklyCap?: number | null;

  // Per-bucket monthly caps.
  outreachMonthlyCap?: number | null;
  ndaCimMonthlyCap?: number | null;
  interestedEscalationsMonthlyCap?: number | null;
  appointmentsMonthlyCap?: number | null;
  totalMonthlyCap?: number | null;

  // Per-bucket daily dollar caps.
  outreachDailyCap?: number | null;
  ndaCimDailyCap?: number | null;
  interestedEscalationsDailyCap?: number | null;
  appointmentsDailyCap?: number | null;
  totalDailyCap?: number | null;

  // Per-KPI daily count limits (only the first N/day pay).
  prospectCreatedDailyCountLimit?: number | null;
  ndaCompletedDailyCountLimit?: number | null;
  cimReceivedDailyCountLimit?: number | null;
  interestedEscalationDailyCountLimit?: number | null;
  completedAppointmentDailyCountLimit?: number | null;

  // Quality multiplier bands (applied at pay-period approval).
  multiplierA: number;
  multiplierB: number;
  multiplierC: number;
  multiplierD: number;
  multiplierF: number;

  // Grade weights (displayed guide; sum = 100).
  gradeWeightContact: number;
  gradeWeightNdaCim: number;
  gradeWeightInterested: number;
  gradeWeightAppointment: number;
  gradeWeightQuality: number;
}

export interface LeadGenPlanDto {
  id: string;
  /** null = the program-default plan; set = a per-generator override. */
  leadGeneratorId?: string | null;
  version: number;
  status: LeadGenPlanStatus;
  effectiveFrom: string;
  effectiveTo?: string | null;
  config: LeadGenPlanConfig;
  notes?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
}

export interface SaveLeadGenPlanRequest {
  /** null = save the program-default plan; set = a per-generator override. */
  leadGeneratorId?: string | null;
  config: LeadGenPlanConfig;
  notes?: string | null;
  /** null = now (must be strictly after the current version's effective date). */
  effectiveFrom?: string | null;
}

export const compensationPlanApi = {
  /** The resolved effective knob-set for a scope as-of a time. Always resolves
   *  (override → program-default → built-in defaults), so it is the safe seed
   *  for the editor even before any version has been saved. Pass the empty GUID
   *  for the program-default scope. */
  effective: (leadGeneratorId: string, asOf?: string) =>
    api.get<LeadGenPlanConfig>("/lead-gen/compensation-plan/effective", {
      leadGeneratorId,
      asOf,
    }),
  /** Current program-default plan version (404 when none saved yet). */
  programDefault: () =>
    api.get<LeadGenPlanDto>("/lead-gen/compensation-plan/program-default"),
  /** Current per-generator override version (404 when none — program default applies). */
  forGenerator: (leadGeneratorId: string) =>
    api.get<LeadGenPlanDto>(`/lead-gen/compensation-plan/generator/${leadGeneratorId}`),
  /** Every version for a scope (program default when leadGeneratorId omitted). */
  versions: (leadGeneratorId?: string) =>
    api.get<LeadGenPlanDto[]>("/lead-gen/compensation-plan/versions", {
      leadGeneratorId,
    }),
  /** Save a new version (supersedes the current open version for the scope). */
  save: (body: SaveLeadGenPlanRequest) =>
    api.post<LeadGenPlanDto>("/lead-gen/compensation-plan", body),
  /** Revert a generator to the program default by removing its open per-generator
   *  override (idempotent — a no-op returning true when no override exists).
   *  Gated on LeadGen.ManagePlan. */
  revert: (leadGeneratorId: string) =>
    api.delete<boolean>(`/lead-gen/compensation-plan/generator/${leadGeneratorId}`),
};
